import { DownloadFile } from '../types/apiUtils';
import { Stats } from '../types/stats';
import { ListApiResponse } from '../types/apiResponse';
import type {
  ApiListParams,
  ApiListParamsWithUnderscores,
  ApiExportParams,
} from '../types/api/listParams';
import { toListParams } from '../types/api/listParams';

function normalizeListParams(
  params?: ApiListParams | ApiListParamsWithUnderscores
): ApiListParams | undefined {
  if (params == null) return undefined;
  return '_scopes' in params ? toListParams(params) : params;
}

export const apiUrl = '/api';

let token: string;
export function setToken(value: string) {
  token = value;
}
export class TrialEndedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TrialEndedError';
  }
}
export class ForbiddenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ForbiddenError';
  }
}

export async function handleResponse(response: Response) {
  if (response.ok) return response.json();
  const error = await response.text();
  if (response.status === 400) {
    throw new Error(error);
  } else if (response.status === 403) {
    throw new ForbiddenError(error);
  } else if (response.status === 402) {
    throw new TrialEndedError(error);
  } else if (response.status === 401) {
    console.error('Token expired');
    window.location.reload();
  }
  throw new Error();
}

export async function handleFileResponse(res: Response) {
  if (res.ok) {
    const filenameExp = /filename="?([^;^"]+)"?;/im;
    const contentDisposition =
      res.headers.get('Content-Disposition') ??
      res.headers.get('content-disposition');
    const matchResult = contentDisposition?.match(filenameExp);
    const filename = matchResult ? matchResult[1] : 'unknown';
    if (filename === 'unknown') {
      throw new Error('Could not retrieve filename');
    }
    return {
      filename: filename,
      blob: await res.blob(),
    };
  } else if (res.status === 400) {
    // So, a server-side validation error occurred.
    // Server side validation returns a string error message, so parse as text instead of json.
    const error = await res.text();
    throw new Error(error);
  }
  throw new Error();
}

export function downloadFile({ filename, blob }: DownloadFile) {
  // 2. Create blob link to download
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename ?? 'download-file'); //due to CORS can't get filename from content-disposition
  // 3. Append to html page
  document.body.appendChild(link);
  // 4. Force download
  link.click();
  // 5. Clean up and remove the link
  link.parentNode?.removeChild(link);
}

// In a real app, would likely call an error logging service.
export function handleError(error: Error) {
  // eslint-disable-next-line no-console
  console.error('API call failed. ' + error);
  throw error;
}

export function apiOptions() {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
}

//Couldn't get JSON server to route with /:id without picking up
//all GET queries, so had to implement this.
//Hopefully will find a fix, and can remove this everywhere in time.
function getSingleRecordUrl(baseUrl: string, id: string) {
  return `${baseUrl}${id}`;
}

export function getSingleRecord<T = unknown>(
  baseUrl: string,
  id: string
): Promise<T> {
  return fetch(getSingleRecordUrl(baseUrl, id), apiOptions())
    .then(handleResponse)
    .catch(handleError);
}

function getStatsUrl(baseUrl: string, id: string): string {
  return `${baseUrl}${id}/stats`;
}

export function getStatsData(baseUrl: string, id: string): Promise<Stats> {
  return fetch(getStatsUrl(baseUrl, id), apiOptions())
    .then(handleResponse)
    .catch(handleError);
}

function getAuditUrl(baseUrl: string, id: string): string {
  return `${baseUrl}${id}/audit`;
}

export function getAuditData(baseUrl: string, id: string) {
  return fetch(getAuditUrl(baseUrl, id), apiOptions())
    .then(handleResponse)
    .catch(handleError);
}

export function saveRecord(baseUrl: string, record: any) {
  return fetch(baseUrl + (record.id || ''), {
    method: record.id ? 'PUT' : 'POST', // POST for create, PUT to update when id already exists.
    headers: { ...apiOptions().headers, 'content-type': 'application/json' },
    body: JSON.stringify({
      ...record,
    }),
  })
    .then(handleResponse)
    .catch(handleError);
}

export function getListUrl(
  baseUrl: string,
  params?: ApiListParams | ApiListParamsWithUnderscores
) {
  const { filters, scopes, sort, order, page, limit } =
    normalizeListParams(params) ?? {};
  let url = `${baseUrl}${
    baseUrl.indexOf('?') > -1 ? '&' : '?'
  }_sort=${sort ?? ''}&_order=${order ?? 'asc'}&_page=${page ?? 1}`;

  if (limit != null && limit > 0) {
    url += `&_limit=${limit}`;
  }

  if (filters) {
    url +=
      '&' +
      Object.keys(filters)
        .filter((key) => filters[key])
        .map((key) => `${key}_like=${filters[key]}`)
        .join('&');
  }
  if (scopes) {
    url += '&' + buildScopeQueryString(scopes);
  }
  return url;
}

export function buildScopeQueryString(scopes: Record<string, string>) {
  return Object.keys(scopes)
    .filter((key) => typeof scopes[key] === 'string' && scopes[key].length > 0)
    .map((key) => {
      const normalizedKey = key.startsWith('s_') ? key.slice(2) : key;
      const value = scopes[key] as string;
      return `${normalizedKey}_scope=${encodeURIComponent(value)}`;
    })
    .join('&');
}

export function getList<T = unknown>(
  baseUrl: string,
  params?: ApiListParams | ApiListParamsWithUnderscores
): Promise<ListApiResponse<T>> {
  return fetch(getListUrl(baseUrl, params), apiOptions())
    .then(handleResponse)
    .catch(handleError);
}

export function deleteRecord(baseUrl: string, id: string | null, data?: any) {
  if (data) {
    return fetch(baseUrl + (id ?? ''), {
      method: 'DELETE',
      headers: { ...apiOptions().headers, 'content-type': 'application/json' },
      body: JSON.stringify({
        ...data,
      }),
    })
      .then(handleResponse)
      .catch(handleError);
  } else {
    return deleteAction(baseUrl + id);
  }
}

export function deleteAction(url: string) {
  return fetch(url, {
    method: 'DELETE',
    headers: { ...apiOptions().headers, 'content-type': 'application/json' },
  })
    .then(handleResponse)
    .catch(handleError);
}

export function postAction(baseUrl: string, record?: any) {
  return fetch(baseUrl, {
    method: 'POST',
    headers: { ...apiOptions().headers, 'content-type': 'application/json' },
    body: JSON.stringify({ ...record }),
  })
    .then(handleResponse)
    .catch(handleError);
}

export function putAction<T = unknown>(
  baseUrl: string,
  record?: any
): Promise<T> {
  return fetch(baseUrl, {
    method: 'PUT',
    headers: { ...apiOptions().headers, 'content-type': 'application/json' },
    body: JSON.stringify({ ...record }),
  })
    .then(handleResponse)
    .catch(handleError);
}

export function upload(baseUrl: string, record: any) {
  return uploadWithMethod(
    baseUrl + (record.id || ''),
    record,
    record.id ? 'PUT' : 'POST'
  );
}
export function uploadWithMethod(baseUrl: string, record: any, method: string) {
  const data = new FormData();
  for (const key in record) {
    if (Array.isArray(record[key])) {
      for (const item in record[key]) {
        data.append(key, record[key][item]);
      }
    } else {
      if (record[key] === 'null' || record[key] === null) {
        record[key] = '';
      }
      data.append(key, record[key]);
    }
  }
  return fetch(baseUrl, {
    method,
    headers: { ...apiOptions().headers },
    body: data,
  })
    .then(handleResponse)
    .catch(handleError);
}

export function getAll(baseUrl: string) {
  return fetch(baseUrl, apiOptions()).then(handleResponse).catch(handleError);
}

export function get(baseUrl: string) {
  let url = `${baseUrl}`;
  return fetch(url, apiOptions()).then(handleResponse).catch(handleError);
}
export function download(url: string): Promise<void> {
  return fetch(url, apiOptions())
    .then(handleFileResponse)
    .then(downloadFile)
    .catch(handleError);
}

export function postArray(url: string, array: any[]) {
  return fetchArray(url, array, 'POST');
}
export function deleteArray(url: string, array: any[]) {
  return fetchArray(url, array, 'DELETE');
}

function fetchArray(url: string, array: any[], method: string) {
  return fetch(url, {
    method: method,
    headers: { ...apiOptions().headers, 'content-type': 'application/json' },
    body: JSON.stringify([...array]),
  })
    .then(handleResponse)
    .catch(handleError);
}

export function exportData(
  url: string,
  sort: string,
  params: ApiExportParams
): Promise<void> {
  return download(getListUrl(url, { ...params, sort }));
}

export function nullBlankProperties(obj: any, excludedProps: any) {
  for (let prop in obj) {
    if (
      obj.hasOwnProperty(prop) &&
      obj[prop] === '' &&
      !excludedProps?.some(prop)
    ) {
      obj[prop] = null;
    }
  }
  return obj;
}

export function removeBlankProperties(obj: any, excludedProps?: any) {
  for (let prop in obj) {
    if (
      obj.hasOwnProperty(prop) &&
      obj[prop] === '' &&
      !excludedProps?.some(prop)
    ) {
      delete obj[prop];
    }
  }
  return obj;
}
